import { Select, type SelectOption } from "@/components/ui/select"
import type { DisplayOp, FieldKind } from "@/lib/predicate-ast"

// Type-aware operator dropdown for one predicate row. The displayed list
// is derived from the field's type per the matrix in the plan; the
// emitted op is always a DisplayOp from predicate-ast.

type Props = {
  fieldType: FieldKind
  value: DisplayOp
  onChange: (next: DisplayOp) => void
  disabled?: boolean
}

export function OperatorPicker({ fieldType, value, onChange, disabled }: Props) {
  const opts = OPERATORS_FOR[fieldType] ?? OPERATORS_FOR.text
  return (
    <Select<string>
      value={opts.find((o) => o.value === value) ?? null}
      onChange={(o) => o && onChange(o.value as DisplayOp)}
      options={opts}
      isDisabled={disabled}
      isSearchable={false}
    />
  )
}

// Source of truth for the matrix. If you edit this, also touch the
// matrix block in app/javascript/lib/predicate-ast.ts and the Forge tool
// schema in Stream C — they encode the same reality.
const TEXT_OPS: SelectOption<string>[] = [
  { value: "eq",      label: "is" },
  { value: "neq",     label: "is not" },
  { value: "matches", label: "contains" },
  { value: "present", label: "is set" },
  { value: "blank",   label: "is not set" },
]

const NUMBER_OPS: SelectOption<string>[] = [
  { value: "eq",      label: "=" },
  { value: "neq",     label: "≠" },
  { value: "gte",     label: "≥" },
  { value: "lte",     label: "≤" },
  { value: "between", label: "between" },
  { value: "present", label: "is set" },
]

const BOOLEAN_OPS: SelectOption<string>[] = [
  { value: "eq",      label: "is true / false" },
  { value: "present", label: "is set" },
]

const DATE_OPS: SelectOption<string>[] = [
  { value: "lt",      label: "before" },
  { value: "gt",      label: "after" },
  { value: "between", label: "between" },
  { value: "present", label: "is set" },
]

const SELECT_OPS: SelectOption<string>[] = [
  { value: "eq",      label: "is" },
  { value: "neq",     label: "is not" },
  { value: "present", label: "is set" },
]

const MULTI_OPS: SelectOption<string>[] = [
  { value: "contains", label: "contains any of" },
  { value: "blank",    label: "is empty" },
  { value: "present", label: "is not empty" },
]

export const OPERATORS_FOR: Record<FieldKind, SelectOption<string>[]> = {
  text:         TEXT_OPS,
  number:       NUMBER_OPS,
  boolean:      BOOLEAN_OPS,
  date:         DATE_OPS,
  select:       SELECT_OPS,
  multi_select: MULTI_OPS,
  system_state: SELECT_OPS,
}
